// useGuardedTap / usePopupGhostGuard — 「その場に現れる UI」のためのタッチのゴーストクリック対策
// (oekaki/CLAUDE.md「タッチのゴーストクリックはオーバーレイを即閉じる」)。
//
// 背景: touch 由来の互換 `click` は、ディスパッチ時点で改めてヒットテストされる。キャンバスの
// ドラッグを離した直後に範囲の近くへ出るボタン群や、`pointerup` 起動のボタンから開いたポップアップは、
// 出た瞬間に自分が後続の click を受け取ってしまう (確定してしまう / 開いた直後に閉じる)。
//
// - `useGuardedTap`: `useTapActivate` の `onClick` 経路を「キーボード / プログラム由来
//   (`detail === 0`) だけ」に絞る。ポインタ由来の起動は `pointerup` (同じボタンで押下が始まった
//   ときだけ) に一本化されるので、ゴーストクリック (押下を伴わない `detail >= 1` の click) では
//   起動しない。
// - `usePopupGhostGuard`: ポップアップ (プルダウン / 小窓) 側のガード。`ConfirmDialog` と同じ規則
//   (開いた後にポップアップ内で始まった pointerdown を伴う click だけ受理、キーボードは常に通す、
//   arm のリセットは render 中に同期で行う) を再利用する。

import { useRef } from 'react';

import { resetArmedOnReopen, shouldAllowConfirmDialogClick } from '../ConfirmDialog.js';
import { type TapHandlers, useTapActivate } from './useTapActivate.js';

/** キーボード / プログラム由来の click か (pointer / touch 由来は `detail >= 1`)。 */
export const isKeyboardOriginClick = (e: { detail: number }): boolean => e.detail === 0;

/** `useTapActivate` + click はキーボード由来だけ通す (ゴーストクリックで起動しない)。 */
export const useGuardedTap = (onActivate: (() => void) | undefined): TapHandlers => {
  const tap = useTapActivate(onActivate);
  return {
    ...tap,
    onClick: (e) => {
      if (!isKeyboardOriginClick(e)) return;
      tap.onClick(e);
    },
  };
};

export interface PopupGhostGuard {
  /** ポップアップのルートに `onPointerDownCapture` で渡す (arm)。 */
  readonly onPointerDownCapture: () => void;
  /** click / change を受理してよいか (`detail` は click / `nativeEvent.detail`)。 */
  readonly allow: (detail: number) => boolean;
}

/** ポップアップが開いている間だけ意味を持つ。`open` の false→true で arm をリセットする。 */
export const usePopupGhostGuard = (open: boolean): PopupGhostGuard => {
  const armedRef = useRef(false);
  const prevOpenRef = useRef(false);
  // render 中に同期でリセット (useEffect だと再オープン直後のゴーストクリックに間に合わない)。
  resetArmedOnReopen(armedRef, prevOpenRef, open);
  return {
    onPointerDownCapture: () => {
      armedRef.current = true;
    },
    allow: (detail) => shouldAllowConfirmDialogClick(armedRef.current, detail),
  };
};
